import React, { useEffect, useState, useMemo, useRef } from "react";
import SimpleTable from "../../../components/tables/simpleTable";
import { useLoadingState } from "../../../components/loadingFeedback";
import {
  callApiAsync,
  getActivitiesEngineering,
  saveActivityEngineering,
  saveEngineeringActActivity
} from "../../../services/apiService";
import { icons } from "../../../constants";
import ModalComponent from "../../../components/modals";
import LaddaButton from "../../../components/buttons/laddaButton";
import { Grid, Box, Typography } from "@mui/material";
import FormRadioButton from "../../../components/formRadioButton";
import { YesNoNAOptions } from "../../../constants/selectData";
import { useForm } from "react-hook-form";
import FormTextField from "../../../components/textField/formTextField";
import { useDispatch } from "react-redux";
import { setEngineeringReportId } from "../../../redux/slices/serviceOrderSlice";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "./styles.css";

const ActivityEnginnerSection = ({
  engineeringActId,
  serviceOrderId,
  isComplete,
  dataER,
  setActivityDataList,
  userId,
  isPageBusy,
  startProtectedAction,
  endProtectedAction,
  onPendingChangesChange
}) => {
  const dispatch = useDispatch();

  const columns = [
    { field: "activityId", header: "", columnType: "hide" },
    { field: "activityName", header: "Actividad" },
    {
      field: "value",
      header: "Si/No",
      columnType: "custom",
      render: (rowData) => {
        let stateName = "";

        switch (rowData.value) {
          case 1:
            stateName = "Sí";
            break;
          case 2:
            stateName = "No";
            break;
          case 3:
            stateName = "N/A";
            break;
          default:
            stateName = "";
            break;
        }

        return (
          <p className="table-default-column w-max-content">{stateName}</p>
        );
      }
    },
    { field: "actions", header: "", columnType: "actionButtons" }
  ];

  const [activityData, setActivityData] = useState([]);
  const [loadingActivities, setLoadingActivities] = useLoadingState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const actionRef = useRef("idle");
  const [isSavingActivity, setIsSavingActivity] = useState(false);

  useEffect(() => {
    if (openModal || actionRef.current !== "closing") return;
    actionRef.current = "idle";
    setIsSavingActivity(false);
    endProtectedAction?.();
  }, [openModal, endProtectedAction]);

  const handleActivitySubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isComplete || !openModal || isPageBusy || actionRef.current !== "idle") return;
    if (startProtectedAction && !startProtectedAction("activity")) return;
    actionRef.current = "running";
    setIsSavingActivity(true);
    try {
      await handleSubmit(
        selectedActivity ? handleStateActivity : handleSaveNewActivity
      )(event);
    } finally {
      if (actionRef.current === "running") {
        actionRef.current = "idle";
        setIsSavingActivity(false);
        endProtectedAction?.();
      }
    }
  };

  const AddvalidationSchema = yup.object().shape({
    newActivity: yup.string().required("Este campo es obligatorio")
  });

  const EditSchema = yup.object().shape({
    selectedOption: yup
      .mixed()
      .oneOf([1, 2, 3], "Seleccione una opción")
      .required("Este campo es obligatorio")
  });

  const validationSchema = useMemo(
    () => (selectedActivity ? EditSchema : AddvalidationSchema),
    [selectedActivity]
  );

  const {
    control,
    formState: { errors, dirtyFields },
    getValues,
    reset,
    setValue,
    handleSubmit
  } = useForm({
    mode: "onTouched",
    resolver: yupResolver(validationSchema),
    defaultValues: { newActivity: "", selectedOption: "" }
  });

  const hasPendingChanges = openModal && Object.keys(dirtyFields).length > 0;
  useEffect(() => {
    onPendingChangesChange?.(hasPendingChanges);
  }, [hasPendingChanges, onPendingChangesChange]);

  const getActivitiesEngineeringData = async (actId = engineeringActId) => {
    await callApiAsync(
      () => getActivitiesEngineering(actId ?? 0),
      (data) => {
        setActivityData(data);
        setActivityDataList(data)
      },
      setLoadingActivities
    );
  };

  const handleOpenModal = (row) => {
    if (isComplete || isPageBusy || actionRef.current !== "idle" || openModal) return;
    setSelectedActivity(row ?? null);
    setOpenModal(true);
  };

  // La tabla cachea las celdas: el onClick de cada fila puede conservar una version
  // anterior de handleOpenModal (con openModal/isPageBusy en true). Siempre se invoca la ultima.
  const handleOpenModalRef = useRef(handleOpenModal);
  handleOpenModalRef.current = handleOpenModal;

  const handleCloseModal = () => {
    if (isPageBusy || actionRef.current !== "idle") return;
    finishCloseModal();
    getActivitiesEngineeringData();
  };

  const finishCloseModal = () => {
    setOpenModal(false);
    reset({ newActivity: "", selectedOption: "" });
  };

  const finishActivitySave = async (data) => {
    dispatch(setEngineeringReportId(data.engineeringActId));
    await getActivitiesEngineeringData(data.engineeringActId);
    actionRef.current = "closing";
    finishCloseModal();
  };

  // Sincronizar formulario al abrir el modal para evitar parpadeo
  useEffect(() => {
    if (!openModal) return;
    if (selectedActivity) {
      const value = selectedActivity.value;
      setValue("selectedOption", value === 1 || value === 2 || value === 3 ? value : "", {
        shouldValidate: false
      });
    } else {
      reset({ newActivity: "", selectedOption: "" }, { keepDefaultValues: true });
    }
  }, [openModal, selectedActivity, setValue, reset]);

  const handleSaveNewActivity = async () => {
    let activityData = {
      requestId: crypto.randomUUID(),
      userId,
      name: getValues("newActivity"),
      engineeringActId: engineeringActId ?? 0,
      serviceOrderId: serviceOrderId,
      made_by_user_id: dataER
        ? dataER.made_by_user_id || dataER.engineer_id || null
        : null
    };
    await callApiAsync(
      () => saveActivityEngineering(activityData),
      finishActivitySave
    );
  };

  const handleStateActivity = async () => {
    let activityData = {
      requestId: crypto.randomUUID(),
      userId,
      id: selectedActivity.engineeringActActivityId ?? 0,
      activityId: selectedActivity.activityId,
      engineeringActId: engineeringActId,
      value: getValues("selectedOption"),
      serviceOrderId: serviceOrderId,
      made_by_user_id: dataER
        ? dataER.made_by_user_id || dataER.engineer_id || null
        : null
    };

    await callApiAsync(
      () => saveEngineeringActActivity(activityData),
      finishActivitySave
    );
  };

  useEffect(() => {
    getActivitiesEngineeringData();
  }, [engineeringActId]);

  return (
    <div className="biomedical-report-activities">
      {!isComplete && (
        <div className="j-end">
          <LaddaButton
            type="button"
            disabled={isPageBusy || isSavingActivity}
            variant="contained"
            onClick={() => {
              handleOpenModal();
            }}
          >
            <div className="IHContainer align-rigth">
              <img className="btn-icon mr-10" src={icons.plusIcon} alt="icon" />
              <p className="button_text">Agregar</p>
            </div>
          </LaddaButton>
        </div>
      )}

      <SimpleTable
        key={isComplete ? "locked" : "editable"}
        loading={loadingActivities}
        text="Evaluar"
        data={activityData}
        columns={columns}
        scrollHeight={"600px"}
        buttons={
          !isComplete
            ? [
              {
                type: "icon",
                icon: icons.editBlue,
                onClick: (data) => handleOpenModalRef.current(data)
              }
            ]
            : []
        }
      />

      <ModalComponent
        open={openModal}
        handleClose={handleCloseModal}
        width="500px"
        title={
          selectedActivity != null
            ? "Editar Actividad"
            : "Agregar Nueva Actividad"
        }
        actions={<></>}
      >
        <Box sx={{ pt: 0.5, pb: 1 }}>
          <form
            onSubmit={handleActivitySubmit}
          >
            <fieldset disabled={isSavingActivity || isPageBusy}
              style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <Grid container spacing={2.5}>
              {!selectedActivity && (
                <Grid item xs={12}>
                  <Typography
                    component="label"
                    variant="body2"
                    sx={{
                      display: "block",
                      mb: 1,
                      color: "#374151",
                      fontWeight: 500,
                      fontSize: "0.875rem"
                    }}
                  >
                    Nombre de la actividad
                  </Typography>
                  <FormTextField
                    name="newActivity"
                    control={control}
                    label=""
                    error={errors.newActivity}
                    margin="0"
                  />
                </Grid>
              )}
              {selectedActivity && (
                <Grid item xs={12}>
                  <FormRadioButton
                    name="selectedOption"
                    control={control}
                    options={YesNoNAOptions}
                    label="Seleccione una opción"
                    onChange={(value) => setSelectedOption(value)}
                    error={errors.selectedOption}
                    labelColor="#374151"
                  />
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: 3,
                paddingTop: 1
              }}
            >
              <LaddaButton type="submit" variant="contained"
                loading={isSavingActivity} disabled={isSavingActivity || isPageBusy}>
                <p className="button_text">
                  {selectedActivity != null ? "Guardar" : "Agregar"}
                </p>
              </LaddaButton>
            </Box>
            </fieldset>
          </form>
        </Box>
      </ModalComponent>
    </div>
  );
};

export default ActivityEnginnerSection;
